import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import {
  BOTTOM_OFFSET,
  defaultGridOptions,
  GridDimensions,
  MAX_SCROLL_BOTTOM_OFFSET,
} from '../helpers';
import { useGridDimensions } from '../hooks';
import { GameItemPlug } from './GameItemPlug';
import { ShowMoreButton } from './ShowMoreButton';
import { GameV1 } from '@/api/games';
import { defaultGameSizesConfig, GameItem } from '@/components/GameItem';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { getGamesPlugs } from '@/helpers/game';
import { useInfiniteScroll } from '@/hooks/scroll';

import { ScGamesGrid, ScGamesGridRow } from '../styled';
import { GameItemSize } from '@/components/GameItem/styled';

export interface GamesGridStaticPros extends GridDimensions {
  games: GameV1[];
  loading?: boolean;
  loadWithScroll?: boolean;
  scrollBottomOffset?: number;
  maxScrollBottomOffset?: number;
  bottomOffset?: number;
  blockScrollHeight?: number;
  plugGray?: boolean;
  noShowMore?: boolean;
  totalCount?: number;
  isSearchModal?: boolean;
  onShowMoreButtonClick?: () => void;
}

export const GamesGridStatic: FC<GamesGridStaticPros> = ({
  perChunkObj,
  gridOptions = defaultGridOptions,
  games,
  loading,
  loadWithScroll = false,
  scrollBottomOffset,
  maxScrollBottomOffset = MAX_SCROLL_BOTTOM_OFFSET,
  bottomOffset = BOTTOM_OFFSET,
  blockScrollHeight,
  plugGray = true,
  gameSizesConfig = defaultGameSizesConfig,
  noShowMore,
  totalCount,
  isSearchModal,
  onShowMoreButtonClick,
}) => {
  const [currentChunk, setCurrentChunk] = useState(1);
  const [gamePlugs, setGamePlugs] = useState<GameV1[]>([]);

  const { perChunk, gridColumns, gameSize } = useGridDimensions({
    perChunkObj,
    gridOptions,
    gameSizesConfig,
  });

  useEffect(() => {
    if (totalCount) return;
    setCurrentChunk(1);
  }, [totalCount, games]);

  const [atTheBottom, continueScroll] = useInfiniteScroll({
    bottomOffset,
    blockScrollHeight,
  });

  const gridArea = useMemo(
    () => currentChunk * perChunk,
    [currentChunk, perChunk]
  );

  const isShowMore = useMemo(() => {
    if (noShowMore) return false;
    if (totalCount) return totalCount > perChunk;
    return games.length > perChunk;
  }, [games.length, noShowMore, perChunk, totalCount]);

  const gamesChunk = useMemo(() => {
    return games.slice(0, isShowMore ? gridArea - 1 : gridArea);
  }, [games, isShowMore, gridArea]);

  const nextChunk = () => {
    setCurrentChunk((s) => s + 1);
  };

  useEffect(() => {
    if (
      loadWithScroll &&
      scrollBottomOffset != null &&
      scrollBottomOffset < maxScrollBottomOffset
    ) {
      nextChunk();
    }
  }, [loadWithScroll, scrollBottomOffset, maxScrollBottomOffset]);

  useEffect(() => {
    if (loadWithScroll && atTheBottom && gamesChunk.length % perChunk === 0) {
      nextChunk();
      continueScroll();
    }
  }, [loadWithScroll, atTheBottom, gamesChunk, perChunk, continueScroll]);

  const isShowMoreMode = useMemo(() => {
    const gamesChunkCount = gamesChunk.length;
    const gamesCount = games.length;

    if (totalCount) {
      return !loadWithScroll && totalCount - gamesChunkCount !== 0;
    }

    return !loadWithScroll && gamesCount - gamesChunkCount !== 0;
  }, [loadWithScroll, games, gamesChunk, totalCount]);

  useEffect(() => {
    if (
      ((totalCount && perChunk > totalCount) || perChunk > games.length) &&
      gamesChunk.length < gridArea
    ) {
      const plugs = getGamesPlugs(gamesChunk, gridColumns, 1);
      setGamePlugs(plugs);
    } else {
      setGamePlugs([]);
    }
  }, [games, totalCount, perChunk, gamesChunk, gridArea, gridColumns]);

  const handleShowMoreButtonClick = () => {
    if (!isShowMoreMode) {
      setCurrentChunk(1);
      return;
    }

    if (onShowMoreButtonClick) {
      onShowMoreButtonClick();
      nextChunk();
    } else {
      nextChunk();
    }
  };

  return (
    <ScGamesGrid>
      {loading && <GamesSkeleton isSearchModal={isSearchModal} />}
      {gamesChunk.length !== 0 && (
        <ScGamesGridRow {...gridOptions}>
          {[...gamesChunk, ...gamePlugs].map((game, index) => (
            <Fragment key={game?.id || index}>
              {game?.id ? (
                <GameItem {...game} gameSizesConfig={gameSizesConfig} />
              ) : (
                <GameItemPlug plugGray={plugGray} />
              )}
            </Fragment>
          ))}
          {isShowMore && (
            <ShowMoreButton
              isShowMoreMode={isShowMoreMode}
              onClick={handleShowMoreButtonClick}
              darkMode={plugGray}
              smallShowBtn={gameSize === GameItemSize.XXS}
            />
          )}
        </ScGamesGridRow>
      )}
    </ScGamesGrid>
  );
};
